import { FlexContainer, ReemaThemeV2 } from '@act/storybook';
import { Box, Slide } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';

export type GoalsSidePanelProps = {
  titleContent: React.ReactNode;
  content: React.ReactNode;
  footerContent: React.ReactNode;
  open: boolean;
  animated?: boolean;
  setShouldRenderSidePanel?: (shouldRender: boolean) => void;
};

type SidePanelContentProps = {
  titleContent: React.ReactNode;
  content: React.ReactNode;
  footerContent: React.ReactNode;
};

const SidePanelContent = forwardRef<HTMLDivElement, SidePanelContentProps>(
  ({ titleContent, content, footerContent }, ref) => {
    return (
      <Box
        ref={ref}
        sx={{
          height: '100%',
          width: '99%',
          backgroundColor: 'surfaces.neutralLighter',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          marginLeft: '8px',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <FlexContainer
            direction="column"
            height="100%"
            padding={3}
            width="100%"
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flexShrink: 0 }}>{titleContent}</Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {content}
            </Box>
          </FlexContainer>
        </Box>
        <Box
          sx={{
            padding: '16px 24px',
            borderTop: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`,
            flexShrink: 0,
            backgroundColor: 'surfaces.neutralLighter',
            width: '95%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {footerContent}
        </Box>
      </Box>
    );
  }
);

export const GoalsSidePanel = ({
  titleContent,
  content,
  footerContent,
  open,
  setShouldRenderSidePanel,
  animated = false,
}: GoalsSidePanelProps) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (animated && open) {
      setShouldRender(true);
    }
  }, [open, animated]);

  if (animated) {
    const handleExited = () => {
      setShouldRender(false);
      setShouldRenderSidePanel?.(false);
    };

    if (!shouldRender) return null;

    return (
      <Slide
        direction="left"
        in={open}
        timeout={225}
        onExited={handleExited}
        mountOnEnter
        unmountOnExit={false}
      >
        <SidePanelContent
          titleContent={titleContent}
          content={content}
          footerContent={footerContent}
        />
      </Slide>
    );
  } else {
    if (!open) return null;
    return (
      <SidePanelContent
        titleContent={titleContent}
        content={content}
        footerContent={footerContent}
      />
    );
  }
};
